import { makeStyles } from "@mui/styles";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useMemo } from "react";
import { E3M3_TO_MCF, M3_TO_BBL, MONTH_LENGTH } from "../helpers/conversionFactors";

const useStyles = makeStyles({
  root: {
    border: 0,
    color: 'text.primary',
    height: 'auto',
    width: '100%',
    overflow: 'hidden'
  },
  tableHeading: {
    marginLeft: 10,
    marginTop: 10,
  }
});

export const EconomicsDataTable = ({ cashflow, forecast, settings }) => {

  // STYLES
  const classes = useStyles();
  const currency = settings?.currency || 'cad';

  const rows = useMemo(() => {
    try {
      const startYear = cashflow[0]?.year;
      const priceEscalation = settings?.priceEscalation;
      let startDateFraction = 1;
      if (settings?.startDate && settings?.startDate?.length > 0) {
        // startYear = Number(new Date(`${settings?.startDate} 12:00:00`)?.getFullYear());
        const startMonth = new Date(`${settings?.startDate} 12:00:00`)?.getMonth() + 1;
        startDateFraction = (12 - startMonth) / 12;
      };
      return cashflow?.map((value, index) => {
        const year = value?.year;

        return ({
          id: 'cashflow-' + index,
          year,
          numSources: value?.numSources || 0,
          oilRate: value.numSources > 0 ? Number(getRate(year, 'oil', forecast)?.toFixed(3)) : 0,
          oilPrice: Number(getPrice(year, settings?.priceOil, currency, settings?.priceOffsetOil, startDateFraction, startYear, priceEscalation)?.toFixed(3)),
          oilRevenue: Number((getRevenue(year, 'oil', cashflow) / 1000)?.toFixed(3)),
          gasRate: value.numSources > 0 ? Number(getRate(year, 'gas', forecast)?.toFixed(3)) : 0,
          gasPrice: Number(getPrice(year, settings?.priceGas, currency, settings?.priceOffsetGas, startDateFraction, startYear, priceEscalation)?.toFixed(3)),
          gasRevenue: Number((getRevenue(year, 'salesGas', cashflow) / 1000)?.toFixed(3)),
          propanePrice: Number(getPrice(year, settings?.pricePropane, currency, settings?.priceOffsetPropane, startDateFraction, startYear, priceEscalation)?.toFixed(3)),
          butanePrice: Number(getPrice(year, settings?.priceButane, currency, settings?.priceOffsetButane, startDateFraction, startYear, priceEscalation)?.toFixed(3)),
          pentanesPrice: Number(getPrice(year, settings?.pricePentanes, currency, settings?.priceOffsetPentanes, startDateFraction, startYear, priceEscalation)?.toFixed(3)),
          nglRevenue: Number((getRevenue(year, 'ngl', cashflow) / 1000)?.toFixed(3)),
          condensateRate: value.numSources > 0 ? Number(getRate(year, 'condensate', forecast)?.toFixed(3)) : 0,
          condensatePrice: Number(getPrice(year, settings?.priceCondensate, currency, settings?.priceOffsetCondensate, startDateFraction, startYear, priceEscalation)?.toFixed(3)),
          condensateRevenue: Number((getRevenue(year, 'condensate', cashflow) / 1000)?.toFixed(3)),
          waterRate: value.numSources > 0 ? Number(getRate(year, 'water', forecast)?.toFixed(3)) : 0,
          cadToUsd: Number(settings?.priceOil?.find(p => Number(new Date(p.date)?.getFullYear()) === year)?.cadToUsd?.toFixed(3)),
          grossRevenue: Number((value?.grossRevenue / 1000)?.toFixed(3)),
          royalty: Number((value?.royalty / 1000)?.toFixed(3)),
          federalIncomeTax: Number((value?.federalIncomeTax / 1000)?.toFixed(3)),
          stateIncomeTax: Number((value?.stateIncomeTax / 1000)?.toFixed(3)),
          incomeTax: Number((value?.incomeTax / 1000)?.toFixed(3)),
          effectiveTaxRate: Number((value?.effectiveTaxRate)?.toFixed(3)),
          adValorem: Number((value?.adValorem / 1000)?.toFixed(3)),
          costs: Number((value?.costs / 1000)?.toFixed(3)),
          capex: Number((value?.capex / 1000)?.toFixed(3)), 
          abandonment: Number((value?.abandonment / 1000 || 0)?.toFixed(3)),
          netRevenue: Number((value?.netRevenue / 1000)?.toFixed(3)),
          netOpCashflow: Number((value?.netOpCashflow / 1000)?.toFixed(3)),
          cashflowBTax: Number((value?.cashflowBTax / 1000)?.toFixed(3)),
          cumCashflowBTax: Number((value?.cumCashflowBTax / 1000)?.toFixed(3)),
          cashflowATax: Number((value?.cashflowATax / 1000)?.toFixed(3)),
          cumCashflowATax: Number((value?.cumCashflowATax / 1000)?.toFixed(3))
        });
      });
    } catch (error) {
    };

    function getRevenue(year, fluid, cashflow) {
      try {
        return Number(((cashflow?.find(c => c?.year === year)?.revenue?.[fluid] || 0))?.toFixed(3));
      } catch (error) {
        return 0;
      };
    };
  
    function getRate(year, fluid, forecast) {
      try {
        if (fluid === 'oil') {
          return Number(
            ((forecast?.byFluid?.oil?.find(f => f?.year === year)?.volume?.p50 || 0) * M3_TO_BBL / (12 * MONTH_LENGTH) )?.toFixed(3));
        } else if (fluid === 'gas') {
          return Number(((forecast?.byFluid?.salesGas?.find(f => f?.year === year)?.volume?.p50 || 0) * E3M3_TO_MCF / (12 * MONTH_LENGTH) )?.toFixed(3));
        } else if (fluid === 'condensate') {
          return Number(((forecast?.byFluid?.condensate?.find(f => f?.year === year)?.volume?.p50 || 0) * M3_TO_BBL / (12 * MONTH_LENGTH) )?.toFixed(3));
        } else if (fluid === 'water') {
          return Number(((forecast?.byFluid?.water?.find(f => f?.year === year)?.volume?.p50 || 0) * M3_TO_BBL / (12 * MONTH_LENGTH) )?.toFixed(3));
        } else {
          return 0;
        };
      } catch (error) {
        return 0;
      };
    };
  }, [cashflow, currency, forecast, settings]);

  

  function getPrice(year, prices, currency, offset, startDateFraction, startYear, rate) {
    try {
      const yearsSinceStart = Math.max(year - startYear, 0);
      let price = prices?.find(p => (new Date(p?.date)?.getFullYear() === year));
      price = price.price * (currency === 'usd' ? price.cadToUsd : 1) + offset * (currency === 'usd' ? price.cadToUsd : 1);
      price = price * Math.pow((1 + rate), (yearsSinceStart + startDateFraction));
      return price;
    } catch (error) {
      return 0;
    };
  };

  const columns = useMemo(() => {
    return [
      {
        field: 'year',
        headerName: 'Year',
        width: 75,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false,
        type: 'string'
      },
      {
        field: 'numSources',
        headerName: 'Well Count',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'oilRate',
        headerName: 'Oil Rate (bbl/d)',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'oilPrice',
        headerName: `Oil Price (${currency === 'usd' ? 'USD' : 'CAD'}/bbl)`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'oilRevenue',
        headerName: `Oil Revenue (M ${currency === 'usd' ? 'USD' : 'CAD'})`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'gasRate',
        headerName: 'Sales Gas Rate (Mscf/d)',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'gasPrice',
        headerName: `Gas Price (${currency === 'usd' ? 'USD' : 'CAD'}/MMBtu)`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'gasRevenue',
        headerName: `Sales Gas Revenue (M ${currency === 'usd' ? 'USD' : 'CAD'})`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'propanePrice',
        headerName: `Propane Price (${currency === 'usd' ? 'USD' : 'CAD'}/bbl)`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'butanePrice',
        headerName: `Butane Price (${currency === 'usd' ? 'USD' : 'CAD'}/bbl)`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'pentanesPrice',
        headerName: `Pentanes+ Price (${currency === 'usd' ? 'USD' : 'CAD'}/bbl)`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'nglRevenue',
        headerName: `NGL Revenue (M ${currency === 'usd' ? 'USD' : 'CAD'})`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'condensateRate',
        headerName: 'Condensate Rate (bbl/d)',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'condensatePrice',
        headerName: `Condensate Price (${currency === 'usd' ? 'USD' : 'CAD'}/bbl)`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'condensateRevenue',
        headerName: `Condensate Revenue (M ${currency === 'usd' ? 'USD' : 'CAD'})`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'waterRate',
        headerName: 'Water Rate (bbl/d)',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'cadToUsd',
        headerName: 'CAD to USD',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'grossRevenue',
        headerName: `Gross Revenue ${currency === 'cad' ? '(M CAD)' : '(M USD)'}`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'royalty',
        headerName: `Royalty ${currency === 'cad' ? '(M CAD)' : '(M USD)'}`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'adValorem',
        headerName: `Ad Valorem ${currency === 'cad' ? '(M CAD)' : '(M USD)'}`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'costs',
        headerName: `OPEX ${settings?.currency === 'cad' ? '(M CAD)' : '(M USD)'}`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'capex',
        headerName: `CAPEX ${settings?.currency === 'cad' ? '(M CAD)' : '(M USD)'}`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'abandonment',
        headerName: `Abandonment ${settings?.currency === 'cad' ? '(M CAD)' : '(M USD)'}`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'netRevenue',
        headerName: `Net Revenue ${settings?.currency === 'cad' ? '(M CAD)' : '(M USD)'}`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'netOpCashflow',
        headerName: `Net Op. Cash Flow ${settings?.currency === 'cad' ? '(M CAD)' : '(M USD)'}`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'cashflowBTax',
        headerName: `Cash Flow b/ Tax ${settings?.currency === 'cad' ? '(M CAD)' : '(M USD)'}`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'cumCashflowBTax',
        headerName: `Cum. Cash Flow b/ Tax ${settings?.currency === 'cad' ? '(M CAD)' : '(M USD)'}`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'federalIncomeTax',
        headerName: `Federal Tax ${currency === 'cad' ? '(M CAD)' : '(M USD)'}`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'stateIncomeTax',
        headerName: `State/Province Tax ${currency === 'cad' ? '(M CAD)' : '(M USD)'}`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'incomeTax',
        headerName: `Income Tax ${currency === 'cad' ? '(M CAD)' : '(M USD)'}`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'cashflowATax',
        headerName: `Cash Flow a/ Tax ${settings?.currency === 'cad' ? '(M CAD)' : '(M USD)'}`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'cumCashflowATax',
        headerName: `Cum. Cash Flow a/ Tax ${settings?.currency === 'cad' ? '(M CAD)' : '(M USD)'}`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'effectiveTaxRate',
        headerName: `Effective Tax Rate (%)`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      }
    ];
  }, [currency, settings?.currency]);

  try {

    return (
      <div className={classes.root}>
        <DataGrid
          rows={rows}
          columns={columns}
          rowsPerPageOptions={[10,25,50,100]}
          disableSelectionOnClick
          className={classes.table}
          components={{
            Toolbar: GridToolbar,
          }}
          sx={{fontSize: 12}}
          rowHeight={24}
          autoHeight={true}
          density="compact"
        />
      </div>
    );
  } catch (error) {
    return <></>
  };

};
